/**
 * D13: Workflow Test Runner Page
 * ===============================
 * Admin page to run automated workflow tests and view results.
 * Buttons: Run All Tests, Run AP Only, Cleanup Data, Full Test Cycle
 */
import { useState } from 'react';
import api from '../api';

interface TestResult {
  workflow_code: string;
  classification: string;
  status: string;
  steps_total: number;
  steps_completed: number;
  steps_skipped: number;
  duration_ms: number;
  instance_id: number | null;
  final_status: string | null;
  error: string | null;
}

interface TestReport {
  run_at: string;
  org_id: number;
  total_workflows: number;
  testable_full: number;
  testable_partial: number;
  definition_only: number;
  passed_full: number;
  failed_full: number;
  passed_partial: number;
  failed_partial: number;
  valid_definitions: number;
  results: TestResult[];
  errors: { workflow_code: string; error: string | null }[];
  duration_ms: number;
  cleanup_counts?: Record<string, number>;
}

const STATUS_COLORS: Record<string, string> = {
  PASSED: 'var(--positive)',
  PASSED_WITH_SKIPS: '#eab308',
  FAILED: '#ef4444',
  VALID_DEFINITION: 'var(--navy-700)',
};

const CLASSIFICATION_LABELS: Record<string, string> = {
  TESTABLE_FULL: 'Full',
  TESTABLE_PARTIAL: 'Partial',
  DEFINITION_ONLY: 'Def Only',
};

export default function WorkflowTestRunner() {
  const [report, setReport] = useState<TestReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [filter, setFilter] = useState<string>('ALL');

  const runTests = async (params: any = {}) => {
    setLoading(true);
    setProgress('Running workflow tests...');
    setReport(null);
    try {
      const { data } = await api.post('/admin/test-runner', params);
      setReport(data);
      setProgress('');
    } catch (err: any) {
      setProgress(`Error: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const runCycle = async () => {
    setLoading(true);
    setProgress('Running full test cycle (cleanup + tests)...');
    setReport(null);
    try {
      const { data } = await api.post('/admin/test-cycle', {});
      setReport(data);
      setProgress('');
    } catch (err: any) {
      setProgress(`Error: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const runCleanup = async () => {
    setLoading(true);
    setProgress('Cleaning up test data...');
    try {
      const { data } = await api.post('/admin/cleanup-test-data');
      setProgress(`Cleaned: ${data.total_deleted} rows deleted`);
    } catch (err: any) {
      setProgress(`Error: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = report?.results?.filter(r => {
    if (filter === 'ALL') return true;
    if (filter === 'FAILED') return r.status === 'FAILED';
    if (filter === 'PASSED') return r.status === 'PASSED' || r.status === 'PASSED_WITH_SKIPS';
    return r.classification === filter;
  }) || [];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>
        Workflow Test Runner
      </h1>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <button
          onClick={() => runTests({})}
          disabled={loading}
          style={{
            padding: '8px 20px', background: 'var(--navy-700)', color: 'var(--text-primary)',
            border: 'none', borderRadius: 6, cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.6 : 1, fontWeight: 600,
          }}
        >
          Run All Tests
        </button>
        <button
          onClick={() => runTests({ departments: ['ACCOUNTS_PAYABLE'] })}
          disabled={loading}
          style={{
            padding: '8px 20px', background: '#8b5cf6', color: 'var(--text-primary)',
            border: 'none', borderRadius: 6, cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.6 : 1, fontWeight: 600,
          }}
        >
          Run AP Only
        </button>
        <button
          onClick={runCycle}
          disabled={loading}
          style={{
            padding: '8px 20px', background: 'var(--positive)', color: 'var(--text-primary)',
            border: 'none', borderRadius: 6, cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.6 : 1, fontWeight: 600,
          }}
        >
          Full Test Cycle
        </button>
        <button
          onClick={runCleanup}
          disabled={loading}
          style={{
            padding: '8px 20px', background: '#dc2626', color: 'var(--text-primary)',
            border: 'none', borderRadius: 6, cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.6 : 1, fontWeight: 600,
          }}
        >
          Clear Test Data
        </button>
      </div>

      {/* Progress */}
      {progress && (
        <div style={{
          padding: 12, marginBottom: 16, borderRadius: 6,
          background: progress.startsWith('Error') ? '#fef2f2' : '#f0f9ff',
          color: progress.startsWith('Error') ? '#dc2626' : '#1e40af',
          border: `1px solid ${progress.startsWith('Error') ? '#fecaca' : '#bfdbfe'}`,
        }}>
          {loading && <span style={{ marginRight: 8 }}>...</span>}
          {progress}
        </div>
      )}

      {/* Summary Cards */}
      {report && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 12, marginBottom: 24,
          }}>
            {[
              { label: 'Total', value: report.total_workflows, color: '#6b7280' },
              { label: 'Full Passed', value: report.passed_full, color: 'var(--positive)' },
              { label: 'Full Failed', value: report.failed_full, color: '#ef4444' },
              { label: 'Partial Passed', value: report.passed_partial, color: '#eab308' },
              { label: 'Partial Failed', value: report.failed_partial, color: '#f97316' },
              { label: 'Valid Defs', value: report.valid_definitions, color: 'var(--navy-700)' },
              { label: 'Duration', value: `${(report.duration_ms / 1000).toFixed(1)}s`, color: '#6b7280' },
            ].map(card => (
              <div key={card.label} style={{
                padding: 16, borderRadius: 8, textAlign: 'center',
                border: `1px solid ${card.color}20`,
                background: `${card.color}08`,
              }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: card.color }}>
                  {card.value}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {card.label}
                </div>
              </div>
            ))}
          </div>

          {/* Filter Tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['ALL', 'PASSED', 'FAILED', 'TESTABLE_FULL', 'TESTABLE_PARTIAL', 'DEFINITION_ONLY'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '4px 12px', borderRadius: 4, border: '1px solid #e5e7eb',
                  background: filter === f ? 'var(--navy-700)' : '#fff',
                  color: filter === f ? '#fff' : '#374151',
                  cursor: 'pointer', fontSize: 13,
                }}
              >
                {f.replace('_', ' ')} ({
                  f === 'ALL' ? report.results.length :
                  f === 'PASSED' ? report.results.filter(r => r.status === 'PASSED' || r.status === 'PASSED_WITH_SKIPS').length :
                  f === 'FAILED' ? report.results.filter(r => r.status === 'FAILED').length :
                  report.results.filter(r => r.classification === f).length
                })
              </button>
            ))}
          </div>

          {/* Results Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Workflow</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>Type</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>Status</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>Steps</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>Final</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px' }}>Time</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Error</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((r, i) => (
                  <tr key={i} style={{
                    borderBottom: '1px solid #f3f4f6',
                    background: i % 2 === 0 ? '#fff' : '#fafafa',
                  }}>
                    <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                      {r.workflow_code}
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11,
                        background: '#f3f4f6', color: '#374151',
                      }}>
                        {CLASSIFICATION_LABELS[r.classification] || r.classification}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        color: STATUS_COLORS[r.status] || '#6b7280',
                      }}>
                        {r.status === 'PASSED_WITH_SKIPS' ? 'PASS*' : r.status === 'VALID_DEFINITION' ? 'VALID' : r.status}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px 12px', fontFamily: 'monospace' }}>
                      {r.steps_completed}/{r.steps_total}
                      {r.steps_skipped > 0 && ` (${r.steps_skipped} skip)`}
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px 12px', fontSize: 11 }}>
                      {r.final_status || '-'}
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px 12px', fontFamily: 'monospace' }}>
                      {r.duration_ms ? `${r.duration_ms}ms` : '-'}
                    </td>
                    <td style={{
                      padding: '6px 12px', fontSize: 11, color: '#dc2626',
                      maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {r.error || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredResults.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              No results match the current filter.
            </div>
          )}
        </>
      )}
    </div>
  );
}
